<template>
  <div class="singleDisbursementModal">
    <el-dialog :visible.sync="dialogVisible" class="diglog-card" :title="$t('data.add_new_bank_account')" :close-on-click-modal="false" width="660px">
      <div class="form-container">
        <el-form ref="form" :rules="rule" :model="form" status-icon label-width="220px" label-position="left">
          <!-- Currency -->
          <el-form-item
            :label="$t('data.currency')"
            prop="settle_type"
          >
            <el-select v-model="form.settle_type" class="select-item" :disabled="isUSDOnlyBankAccount" @change="onCurrencyChange">
              <el-option
                v-for="item in currencyList"
                :key="item.value"
                :label="item.label"
                :value="item.value"
              />
            </el-select>
          </el-form-item>

          <!-- Account Location -->
          <el-form-item
            :label="$t('data.account_location')"
            prop="settlement_country"
          >
            <el-select v-model="form.settlement_country" class="select-item" :disabled="form.settle_type === 1 || (form.settle_type === 2 && isLocalCurrencyUSD)">
              <el-option
                v-for="item in accountLocationList"
                :key="item.value"
                :label="item.label"
                :value="item.value"
              />
            </el-select>
          </el-form-item>

          <!-- Bank Name -->
          <el-form-item
            :label=" $t('merchant.account_bank') "
            prop="settlement_bank"
          >
            <el-input
              v-if="form.settle_type === 2"
              v-model="form.settlement_bank"
              clearable
              autocomplete="off"
              placeholder="Enter bank name"
            />
            <el-select
              v-else
              v-model="form.settlement_bank"
              filterable
              class="select-item"
              clearable
            >
              <el-option
                v-for="(item,index) in bankList"
                :key="index"
                :label="item.bank_code + '-' + item.full_name"
                :value="item.bank_code"
              />
            </el-select>
          </el-form-item>

          <!-- Bank Account Number -->
          <el-form-item
            v-if="$store.getters.curCountry.country_code === 'PH'"
            :label="$t('merchant.account_num')"
            prop="settlement_bank_account_num_ph"
          >
            <el-input v-model="form.settlement_bank_account_num_ph" clearable type="text" class="input-item" placeholder="Enter Account" autocomplete="off" @change="bankAccountPH()" />
          </el-form-item>
          <el-form-item
            v-else
            :label="$t('merchant.account_num')"
            prop="settlement_bank_account_num"
          >
            <el-input v-model="form.settlement_bank_account_num" clearable type="text" class="input-item" placeholder="Enter Account" autocomplete="off" />
          </el-form-item>

          <!-- Bank Account Holder -->
          <el-form-item :label="$t('data.bank_account_holder')" prop="settlement_bank_account_name">
            <el-input v-model="form.settlement_bank_account_name" clearable autocomplete="off" placeholder="Enter Account Holder" />
          </el-form-item>

          <!-- Bank Address -->
          <el-form-item
            :label="$t('data.bank_address')"
            prop="settlement_bank_address"
          >
            <el-input v-model="form.settlement_bank_address" clearable autocomplete="off" :placeholder="$t('data.bank_address')" />
          </el-form-item>

          <!-- SWIFT Code -->
          <el-form-item
            :label="$t('data.swift_code')"
            prop="swift"
          >
            <el-input v-model="form.swift" clearable autocomplete="off" :placeholder="$t('data.swift_code')" />
          </el-form-item>
          <el-form-item prop="attachment_name">
            <template slot="label"><span style="color: #71110e;margin-right: 4px;">*</span>{{ $t('data.bank_account_proof') }}
              <el-tooltip placement="top" :content="$t('data.bank_wallet_tooltip')">
                <i class="el-icon-question" style="color: #909399; margin-left: 4px; cursor: pointer;" />
              </el-tooltip>
              <br>
            </template>
            <div>
              <div v-if="form.attachment_name" style="margin-bottom: 10px">
                <div v-if="isImg(form.attachment_content)" class="content-file">
                  <!-- <img height="150" :src="form.attachment_content_oss_url"> -->
                  <el-image
                    style="height:150px"
                    :src="form.attachment_content_oss_url"
                    :preview-src-list="[form.attachment_content_oss_url]"
                  />
                </div>
                <span v-else class="content-file">{{ form.attachment_name }}</span>
                <el-button type="danger" size="mini" style="margin-left: 10px" icon="el-icon-delete" @click="onClickRemoveBtn()">{{ $t('components.delete') }}</el-button>
              </div>
              <UploadFileBtn v-else @templateFileSuccess="templateFileSuccess" v-on="$listeners" />
            </div>
          </el-form-item>
        </el-form>
      </div>

      <div slot="footer" class="dialog-footer">
        <!-- <el-button @click="dialogVisible = false">{{ $t('components.cancel') }}</el-button> -->
        <el-button type="info" :disabled="submiting" class="btn-submit" @click="onClickSubmitBtn">{{ $t('components.submit') }}</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { fetchDisbursementAllCodeList, getAllCountry } from '@/api/components/components.js'
import { PostBankAdd } from '@/api/data/data'
import { GULF_USD_ONLY_COUNTRY_CODES } from '@/const/const'
import store from '@/store'
export default {
  name: 'Add_Bank_Account_Modal',
  components: {
    UploadFileBtn: () => import('@/views/components/Upload/UploadFileBtn.vue')
  },
  data() {
    const validAttachmentName = (rule, value, callback) => {
      if (!this.form.attachment_name) {
        return callback(new Error('Please upload file'))
      }
      callback()
    }

    const checkNumberType = (rule, value, callback) => {
      if (/\s/g.test(value)) {
        return callback(new Error('The input value cannot contain spaces'))
      }
      return callback()
    }

    const validateBankAddress = (rule, value, callback) => {
      if (this.form.settle_type === 2 && !value) {
        return callback(new Error(this.$t('data.bank_address_required')))
      }
      callback()
    }

    const validateSwiftCode = (rule, value, callback) => {
      if (this.form.settle_type === 2 && !value) {
        return callback(new Error(this.$t('data.swift_code_required')))
      }
      callback()
    }

    const validateAccountLocation = (rule, value, callback) => {
      if (!value) {
        return callback(new Error(this.$t('data.account_location_required')))
      }
      callback()
    }

    return {
      channelList: [],
      bankList: [],
      countryList: [], // 所有国家列表
      urlList: [],
      repaymentTypeArr: [{ label: 'Bill Payment', value: 'Bill Payment' }, { label: 'Qrph Code', value: 'Qrph Code' }, { label: 'Bank Transfer', value: 'Bank Transfer' }],
      form: {
        attachment_name: null,
        attachment_content: null,
        attachment_content_oss_url: null,
        settle_type: 1, // 结算类型：1=本币，2=美元
        settlement_country: null, // 银行卡归属地国家代码
        settlement_bank: null,
        settlement_bank_account_num: null,
        settlement_bank_account_num_ph: null,
        settlement_bank_account_name: null,
        settlement_bank_address: null,
        swift: null
      },
      settlement_country: '',
      dialogVisible: false,
      submiting: false,
      rule: {
        settle_type: [
          { required: true, message: this.$t('data.currency_required'), trigger: ['blur', 'change'] }
        ],
        settlement_country: [
          { validator: validateAccountLocation, trigger: ['blur', 'change'] }
        ],
        settlement_bank: [
          { required: true, message: 'Bank Name cannot be empty', trigger: ['blur', 'change'] }
        ],
        settlement_bank_account_num: [
          { required: true, message: 'Bank Account No. cannot be empty', trigger: ['blur', 'change'] },
          { min: 5, max: 50, message: 'Invalid format.' },
          { validator: checkNumberType, trigger: 'blur' }
        ],
        settlement_bank_account_num_ph: [
          { required: true, message: 'Bank Account No. cannot be empty', trigger: ['blur', 'change'] },
          { min: 5, max: 50, message: 'Invalid format.' },
          { validator: checkNumberType, trigger: 'blur' },
          { pattern: /^\d+$/, message: 'Invalid format.' }
        ],
        settlement_bank_account_name: [
          { required: true, message: 'Bank Account Holder cannot be empty', trigger: ['blur', 'change'] },
          { max: 100, message: 'Bank Account Holder cannot exceed 100 characters.' }
        ],
        attachment_name: [
          { validator: validAttachmentName, message: 'Please upload file', trigger: 'blur' }
        ],
        settlement_bank_address: [
          { validator: validateBankAddress, trigger: ['blur', 'change'] }
        ],
        swift: [
          { validator: validateSwiftCode, trigger: ['blur', 'change'] }
        ],
        verification_code: [
          { required: true, message: 'MFA Verification Code cannot be empty', trigger: ['blur', 'change'] }
        ]
      }
    }
  },
  computed: {
    // 判断当前本币是否为 USD
    isLocalCurrencyUSD() {
      const currentCurrency = this.$store.getters.curCountry?.currency || this.$store.getters.currency || ''
      return currentCurrency.toUpperCase() === 'USD'
    },
    // 海湾六国添加银行账户时只允许添加美元账户
    isGulfUSDOnlyCountry() {
      const countryCode = this.$store.getters.curCountry?.country_code || ''
      return GULF_USD_ONLY_COUNTRY_CODES.includes(countryCode)
    },
    // USD-only 国家仅展示美元入口
    isUSDOnlyBankAccount() {
      return this.isLocalCurrencyUSD || this.isGulfUSDOnlyCountry
    },
    currencyList() {
      // 获取当前币种，如果不存在则使用默认值
      const currentCurrency = this.$store.getters.curCountry?.currency || this.$store.getters.currency || ''

      // 本币为 USD 或海湾六国时，只显示 USD 选项
      if (this.isUSDOnlyBankAccount) {
        return [
          { label: 'USD', value: 2 }
        ]
      }

      return [
        { label: currentCurrency, value: 1 },
        { label: 'USD', value: 2 }
      ]
    },
    // 根据币种返回可选的国家列表
    accountLocationList() {
      // 根据当前语言决定使用中文名还是英文名
      const useCnName = this.$store.getters.language === 'zh'
      // 获取当前币种代码
      const currentCurrency = this.$store.getters.curCountry?.currency || this.$store.getters.currency || ''

      // 当币种为本币时，只显示当前币种对应的本地国家
      if (this.form.settle_type === 1) {
        const currentCountry = this.countryList.find(
          country => country.currency === currentCurrency
        )
        return currentCountry ? [{ label: useCnName ? currentCountry.cn_name : currentCountry.en_name, value: currentCountry.code }] : []
      } else {
        // 当币种为USD时，显示所有国家
        return this.countryList.map(country => ({
          label: useCnName ? country.cn_name : country.en_name,
          value: country.code
        }))
      }
    }
    // isHasAmount() {
    //   const channelCode = this.form.channel_code || ''
    //   return !channelCode.includes('STATIC')
    // },
  },
  watch: {
    'form.settle_type': {
      handler(newVal) {
        const currentCurrency = this.$store.getters.curCountry?.currency || this.$store.getters.currency || ''

        if (newVal === 2) {
          // USD币种时
          if (this.isLocalCurrencyUSD) {
            // 本币是USD时，归属地固定为中国香港
            const hkCountry = this.countryList.find(country => country.code === 'HK')
            this.form.settlement_country = hkCountry ? hkCountry.code : null
          } else {
            // 本币不是USD时，银行账户归属地需要用户选择，清空当前选择
            this.form.settlement_country = null
          }
          // 切换到USD时清空银行名称
          this.form.settlement_bank = ''
        } else {
          // 本地币种时，银行账户归属地固定为本地国家
          const currentCountry = this.countryList.find(
            country => country.currency === currentCurrency
          )
          this.form.settlement_country = currentCountry ? currentCountry.code : null
          // 切换到本地币种时清空银行名称
          this.form.settlement_bank = ''
        }
        // 清除验证状态，避免切换币种时显示验证提示
        this.$nextTick(() => {
          if (this.$refs.form) {
            this.$refs.form.clearValidate('settlement_country')
            this.$refs.form.clearValidate('settlement_bank_address')
            this.$refs.form.clearValidate('swift')
            this.$refs.form.clearValidate('settlement_bank')
          }
        })
      },
      immediate: false
    }
  },
  created() {
    store.dispatch('user/getInfo').then(res => {
      this.settlement_country = res.country_code
    })
    this.getBankList()
    this.getCountryList()
  },
  methods: {
    // getAllChannelList() {
    //   fetchAvailableChannelList().then(res => {
    //     if (res.code === 10000) {
    //       this.channelList = res.data
    //     }
    //   })
    // },
    isImg(value) {
      return /\.(gif|jpg|jpeg|png|GIF|JPG|PNG)$/.test(value)
    },
    // 获取所有国家列表
    getCountryList() {
      getAllCountry().then(res => {
        this.countryList = res.data || []
        // 初始化时设置默认归属地
        const currentCurrency = this.$store.getters.curCountry?.currency || this.$store.getters.currency || ''

        if (this.form.settle_type === 1) {
          // 本币时，设置为当前国家
          const currentCountry = this.countryList.find(
            country => country.currency === currentCurrency
          )
          this.form.settlement_country = currentCountry ? currentCountry.code : null
        } else if (this.form.settle_type === 2 && this.isLocalCurrencyUSD) {
          // 本币是 USD 时，归属地固定为中国香港
          const hkCountry = this.countryList.find(country => country.code === 'HK')
          this.form.settlement_country = hkCountry ? hkCountry.code : null
        }
      })
    },
    getBankList() {
      fetchDisbursementAllCodeList().then(res => {
        const data = res.data
        this.bankList = data
        // if (res.code === 10000) {
        //   const data = res.data
        //   this.bankList = data
        // }
      })
    },
    onClickFileName(url) {
      window.open(url, '_bank')
    },
    onClickRemoveBtn() {
      this.form.attachment_name = undefined
      this.form.attachment_content = undefined
      this.form.attachment_content_oss_url = undefined
      this.$forceUpdate()
    },
    templateFileSuccess(data = {}) {
      this.form.attachment_name = data.fileName
      this.form.attachment_content = data.relativePath
      this.form.attachment_content_oss_url = data.absolutePath
      this.$forceUpdate()
      if (this.$refs['form']) {
        this.$refs['form'].clearValidate('attachment_name')
      }
    },
    // 改变弹框状态
    changeModalStatus(flag) {
      this.dialogVisible = flag
      const currentCurrency = this.$store.getters.curCountry?.currency || this.$store.getters.currency || ''

      // 本币为 USD 或海湾六国时，默认选择 USD（settle_type = 2）
      const defaultSettleType = this.isUSDOnlyBankAccount ? 2 : 1

      // 当本币是 USD 且选择 USD 时，归属地固定为中国香港
      // 当币种为本币（非 USD）时，归属地默认为当前国家
      // 当本币不是 USD 但选择 USD 时，归属地由用户选择，默认为 null
      let defaultAccountLocation = null
      if (defaultSettleType === 1) {
        // 本币（非 USD）情况
        defaultAccountLocation = this.countryList.find(country => country.currency === currentCurrency)?.code || null
      } else if (this.isLocalCurrencyUSD) {
        // 本币是 USD 情况，归属地固定为中国香港
        defaultAccountLocation = this.countryList.find(country => country.code === 'HK')?.code || null
      }

      this.form = {
        attachment_name: null,
        attachment_content: null,
        attachment_content_oss_url: null,
        settle_type: defaultSettleType,
        settlement_country: defaultAccountLocation,
        settlement_bank: null,
        settlement_bank_account_num: null,
        settlement_bank_account_num_ph: null,
        settlement_bank_account_name: null,
        settlement_bank_address: null,
        swift: null
      }
      this.$nextTick(() => this.$refs.form.clearValidate())
    },
    onClickSubmitBtn() {
      if (this.isGulfUSDOnlyCountry && this.form.settle_type !== 2) {
        this.form.settle_type = 2
        this.form.settlement_country = null
      }
      this.$refs.form.validate((valid) => {
        if (valid) {
          this.submiting = true
          const params = this.form
          PostBankAdd(params).then(res => {
            this.$message.success('Success')
            this.dialogVisible = false
            this.$emit('submit')
          }).finally(() => {
            this.submiting = false
          })
        }
      })
    },
    bankAccountPH() {
      this.form.settlement_bank_account_num = this.form.settlement_bank_account_num_ph
    },
    onCurrencyChange(value) {
      // This method is called when currency changes
      // The actual logic is handled by the watcher
    }
  }
}
</script>

<style scoped>
.singleDisbursementModal /deep/ .el-input--suffix .el-input__inner {
  padding-right: 60px;
}
.icon-question{
  position: relative;
  top: -1px;
  margin-left: 5px;
  font-size: 12px;
}
.select-item{
  width: 100%;
}
.tip{
  font-size: 14px;
  font-weight: 400;
  color: #303133;
}
.dialog-footer{
  text-align: center;
}
.btn-submit{
  width: 100%;
  height: 40px;
  border-radius: 4px;
}
.content-file{
  display: inline-block;
  width: 200px;
  vertical-align: middle;
  color:#3855F1;
  text-decoration: underline;
  cursor: pointer;
  overflow:hidden;
  text-overflow:ellipsis;
  white-space:nowrap;
}

</style>
